import { compact } from 'lodash'

import { transformCurrencyAmountDto } from '@marketplace-web/currency/currency-data'

import {
  getDominantColor,
  getItemThumbnail,
  ItemThumbnailSize,
} from '@marketplace-web/shared/item-photo-data'
import { transformItemBoxDto } from '@marketplace-web/item-box/product-item-data'

import { BlockEntityType } from '../constants'
import { HomepageConfigResp } from '../types/api/response/homepage-config-resp'
import {
  ElementSelectionDto,
  HomepageBlockEntityDto,
  HomepageItemBlockCtaDto,
  HomepageItemBlockDto,
} from '../types/dtos/homepage-blocks'
import { HomepageItemDto } from '../types/dtos/homepage-item'
import {
  ElementsSelectionModel,
  HomepageBlockEntityModel,
  HomepageItemBlockCtaModel,
  HomepageItemBlockModel,
  HomepageItemModel,
  HomepageTabModel,
} from '../types/models/homepage-blocks'
import { transformBannersBlock, transformThumbnailsBlock } from './homepage-layouts'
import { HomepageTabResp } from '../types/homepage-blocks'

const transformHomepageItemBlockCta = (
  cta: HomepageItemBlockCtaDto | null,
): HomepageItemBlockCtaModel | null => {
  if (!cta) return null

  return {
    url: cta.url,
    title: cta.title,
    accessibilityLabel: cta.accessibility_label,
  }
}

const transformHomepageItemDto = (item: HomepageItemDto): HomepageItemModel => ({
  id: item.id,
  title: item.title,
  user: {
    id: item.user_id,
    isBusiness: item.business_user,
  },
  url: item.url,
  favouriteCount: item.favourite_count,
  price: transformCurrencyAmountDto(item.price),
  serviceFee: item.service_fee ? transformCurrencyAmountDto(item.service_fee) : undefined,
  totalItemPrice: item.total_item_price
    ? transformCurrencyAmountDto(item.total_item_price)
    : undefined,
  isPromoted: item.promoted,
  isFavourite: item.is_favourite,
  thumbnailUrl: item.photo && getItemThumbnail([item.photo], ItemThumbnailSize.Large),
  dominantColor: item.photo && getDominantColor([item.photo]),
  itemBox: item.item_box && transformItemBoxDto(item.item_box),
  contentSource: item.content_source,
  metadata: item.metadata,
})

export const transformHomepageItemDtos = (
  items: Array<HomepageItemDto>,
): Array<HomepageItemModel> => items.map(transformHomepageItemDto)

export const transformHomepageItemBlock = (
  itemBlock: HomepageItemBlockDto,
): HomepageItemBlockModel => ({
  id: itemBlock.id,
  name: itemBlock.name,
  title: itemBlock.title,
  subtitle: itemBlock.subtitle,
  items: transformHomepageItemDtos(itemBlock.items),
  headerCta: transformHomepageItemBlockCta(itemBlock.header_cta),
  itemCta: transformHomepageItemBlockCta(itemBlock.item_cta),
})

const transformElementsSelectionDto = ({
  action_title,
  post_action,
  ...rest
}: ElementSelectionDto): ElementsSelectionModel => ({
  actionTitle: action_title,
  postAction: {
    title: post_action.title,
    subtitle: post_action.subtitle,
    actionTitle: post_action.action_title,
  },
  ...rest,
})

export const transformHomepageBlockEntity = (
  itemBlock: HomepageBlockEntityDto,
  logHomeError: (error: Error | null, feature?: string) => void,
): HomepageBlockEntityModel | undefined => {
  const { type } = itemBlock

  try {
    switch (type) {
      case BlockEntityType.ItemBoxBlock:
        return { type, entity: transformHomepageItemBlock(itemBlock.entity) }
      case BlockEntityType.ThumbnailsBlock:
        return { type, entity: transformThumbnailsBlock(itemBlock.entity) }
      case BlockEntityType.ExposureBlock:
        return { type, entity: itemBlock.entity }
      case BlockEntityType.BannersBlock:
        return { type, entity: transformBannersBlock(itemBlock.entity) }
      case BlockEntityType.Item:
        return { type, entity: transformHomepageItemDto(itemBlock.entity) }
      case BlockEntityType.AdOrCloset:
        return { type, entity: null }
      case BlockEntityType.Header:
        return itemBlock.entity.title ? { type, entity: itemBlock.entity } : undefined
      case BlockEntityType.ListerActivationBanner:
        return { type, entity: null }
      case BlockEntityType.ElementsSelection:
        return { type, entity: transformElementsSelectionDto(itemBlock.entity) }
      default:
        return undefined
    }
  } catch (err) {
    logHomeError(err, type)

    return undefined
  }
}

export const transformHomepageBlockEntities = (
  blocks: Array<HomepageBlockEntityDto>,
  logHomeError: (error: Error | null, feature?: string) => void,
): Array<HomepageBlockEntityModel> =>
  compact(blocks.map(block => transformHomepageBlockEntity(block, logHomeError)))

export const transformHomepageTabDtos = (
  tabs: HomepageConfigResp['verticals'],
): Array<HomepageTabModel> =>
  tabs.map(tab => ({
    title: tab.title,
    name: tab.name,
    isPromoBoxEnabled: tab.is_promobox_enabled,
    isSellerPromotionEnabled: tab.is_seller_promotion_enabled,
    feed: {
      title: tab.feed.title,
      arePromotedClosetsEnabled: tab.feed.are_promoted_closets_enabled,
      areAdsEnabled: tab.feed.are_ads_enabled,
      isPromoBoxEnabled: tab.feed.is_promobox_enabled,
    },
  }))

export const transformHomepageBlockResponse = (
  response: HomepageTabResp,
  logHomeError: (error: Error | null, feature?: string) => void,
) => ({
  blocks: transformHomepageBlockEntities(response.blocks, logHomeError),
  showLoadMoreButton: response.load_more_button,
  nextPageToken: response.pagination.next_page_token,
})
